import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, TrashIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { attributes } from "@blis/shared/schemas/test-result-spec";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { mutationResponse } from "@/utils/trpc";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

type TestResultSpec = RouterOutputs["admin"]["testResultSpec"]["get"];
type TestResultAttributeSpec =
  RouterOutputs["admin"]["testResultSpec"]["attributes"]["list"][number];

export function ResultAttributesTable({
  resultSpec,
}: {
  resultSpec: NonNullable<TestResultSpec>;
}) {
  const { data } = trpc.admin.testResultSpec.attributes.list.useQuery({
    testResultSpecId: resultSpec.id,
  });

  const { options } = useAdminGridOptions(
    data,
    [
      { field: "name" },
      { field: "type", maxWidth: 180 },
      { field: "code", maxWidth: 180 },
      { field: "isRequired", headerName: "Required", maxWidth: 120 },
      {
        colId: "actions",
        headerName: "",
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: (params) => {
          return (
            <div className="flex h-full items-center gap-2 justify-end">
              <ResultAttributeFormDialog
                attributeSpec={params.data}
                resultSpec={resultSpec}
              >
                <Button variant="ghost" size="icon-sm">
                  <PencilIcon />
                </Button>
              </ResultAttributeFormDialog>
              <RemoveAttributeDialog attributeSpec={params.data}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive hover:text-destructive"
                >
                  <TrashIcon />
                </Button>
              </RemoveAttributeDialog>
            </div>
          );
        },
      },
    ],
    { defaultColDef: { flex: 1 } },
  );
  return (
    <TabLayout
      description="Configure attributes for the test result spec"
      actions={
        <ResultAttributeFormDialog resultSpec={resultSpec}>
          <Button>Add attribute</Button>
        </ResultAttributeFormDialog>
      }
    >
      <div className="flex h-96">
        <AdminGrid {...options} />
      </div>
    </TabLayout>
  );
}

type AttributeFormValues = z.infer<typeof attributes.create>;

const attributeTypes = [
  { label: "Text", value: "TEXT" },
  { label: "Numeric", value: "NUMERIC" },
  { label: "Boolean", value: "BOOLEAN" },
];

function ResultAttributeFormDialog({
  children,
  attributeSpec,
  resultSpec,
}: {
  children: ReactNode;
  attributeSpec?: TestResultAttributeSpec;
  resultSpec: { id: number };
}) {
  const [open, setOpen] = useState(false);

  const create = trpc.admin.testResultSpec.attributes.create.useMutation(
    mutationResponse({
      errorTitle: "Failed to create attribute",
      successMessage: "Attribute created successfully",
      onSuccess() {
        setOpen(false);
      },
    }),
  );
  const update = trpc.admin.testResultSpec.attributes.update.useMutation(
    mutationResponse({
      errorTitle: "Failed to update attribute",
      successMessage: "Attribute updated successfully",
      onSuccess() {
        setOpen(false);
      },
    }),
  );

  const methods = useForm({
    resolver: zodResolver(attributes.create),
    defaultValues: {
      testResultSpecId: resultSpec.id,
      name: attributeSpec?.name ?? "",
      code: attributeSpec?.code ?? "",
      type: attributeSpec?.type ?? ("TEXT" as const),
      isRequired: attributeSpec?.isRequired ?? false,
    },
  });

  const onSubmit: SubmitHandler<AttributeFormValues> = async (values) => {
    if (attributeSpec) {
      await update.mutateAsync({ ...values, id: attributeSpec.id });
    } else {
      await create.mutateAsync({
        ...values,
        testResultSpecId: resultSpec.id,
      });
    }
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>
              {attributeSpec ? "Edit" : "Add"} attribute
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              id="add-attribute-form"
              className="flex flex-col gap-3"
            >
              <Controller
                control={methods.control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    validationBehavior="aria"
                    isInvalid={!!error}
                  >
                    <Label>Name</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Controller
                control={methods.control}
                name="code"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    validationBehavior="aria"
                    isInvalid={!!error}
                  >
                    <Label>Code</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Controller
                control={methods.control}
                name="type"
                render={({ field, fieldState: { error } }) => (
                  <Select
                    selectedKey={field.value ?? ""}
                    onSelectionChange={(key) => field.onChange(key)}
                    validationBehavior="aria"
                    isInvalid={!!error}
                  >
                    <Label>Type</Label>
                    <SelectTrigger />
                    <SelectList items={attributeTypes}>
                      {(item) => (
                        <SelectItem id={item.value} textValue={item.label}>
                          {item.label}
                        </SelectItem>
                      )}
                    </SelectList>
                  </Select>
                )}
              />
              <Controller
                control={methods.control}
                name="isRequired"
                render={({ field }) => (
                  <CheckboxField>
                    <Checkbox
                      isSelected={field.value}
                      onChange={(flag) => field.onChange(flag)}
                    >
                      Required
                    </Checkbox>
                    <Description>
                      The attribute is required along with result entry.
                    </Description>
                  </CheckboxField>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-attribute-form"
              isDisabled={methods.formState.isSubmitting}
              isPending={methods.formState.isSubmitting}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function RemoveAttributeDialog({
  attributeSpec,
  children,
}: {
  attributeSpec: TestResultAttributeSpec;
  children: ReactNode;
}) {
  const remove = trpc.admin.testResultSpec.attributes.delete.useMutation(
    mutationResponse({
      errorTitle: "Failed to remove attribute",
      successMessage: "Attribute removed successfully",
    }),
  );

  return (
    <ConfirmPrompt
      danger
      title="Remove attribute"
      message="Are you sure you want to remove this attribute?"
      loading={remove.isPending}
      onConfirm={() => {
        remove.mutate({ id: attributeSpec.id });
      }}
    >
      {children}
    </ConfirmPrompt>
  );
}
